import React, { useState, useEffect } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import { PUBLIC_URLS, getResourceUrl } from '../config/apiConfig';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';

function OfficeBearersPage() {
  const [officeBearers, setOfficeBearers] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'past'
  const [pastOfficeBearersByYear, setPastOfficeBearersByYear] = useState([]);
  const [expandedYears, setExpandedYears] = useState(new Set());
  
  // Fetch current office bearers
  useEffect(() => {
    const fetchOfficeBearers = async () => {
      if (viewMode !== 'current') return;
      
      setIsLoading(true);
      try {
        const response = await fetch(`${PUBLIC_URLS.OFFICE_BEARERS}`);
        if (!response.ok) {
          throw new Error('Failed to fetch office bearers');
        }
        const data = await response.json();
        
        // Separate advisors from other office bearers
        const allMembers = data.items || [];
        const advisorMembers = allMembers.filter(member => 
          member.position && member.position.toLowerCase().includes('advisor')
        );
        const regularMembers = allMembers.filter(member => 
          !member.position || !member.position.toLowerCase().includes('advisor')
        );
        
        setOfficeBearers(regularMembers);
        setAdvisors(advisorMembers);
        setError(null);
      } catch (error) {
        console.error('Error fetching office bearers:', error);
        setError('Failed to load office bearers. Please try again later.');
        setOfficeBearers([]);
        setAdvisors([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOfficeBearers();
  }, [viewMode]);
  
  // Fetch all past office bearers organized by year
  useEffect(() => {
    const fetchPastOfficeBearers = async () => {
      if (viewMode !== 'past') return;
      
      setIsLoading(true);
      try {
        const response = await fetch(`${PUBLIC_URLS.OFFICE_BEARERS_PAST_BY_YEAR}`);
        if (!response.ok) {
          throw new Error('Failed to fetch past office bearers');
        }
        const data = await response.json();
        
        if (data.success) {
          setPastOfficeBearersByYear(data.data || []);
          // Expand all years by default
          if (data.data && data.data.length > 0) {
            setExpandedYears(new Set(data.data.map(yearData => yearData.year)));
          }
        } else {
          throw new Error(data.message || 'Failed to fetch past office bearers');
        }
        
        setError(null);
      } catch (error) {
        console.error('Error fetching past office bearers:', error);
        setError('Failed to load past office bearers. Please try again later.');
        setPastOfficeBearersByYear([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPastOfficeBearers();
  }, [viewMode]);
  
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setError(null);
  };
  
  // Term period of the committee currently on display, e.g. "April 2026 to March 2027"
  const currentTermLabel = (() => {
    const member = [...officeBearers, ...advisors].find(m => m.term_start);
    if (!member) return null;

    const format = (value) => new Date(value).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return member.term_end
      ? `${format(member.term_start)} to ${format(member.term_end)}`
      : `Since ${format(member.term_start)}`;
  })();

  const toggleYearExpansion = (year) => {
    const newExpandedYears = new Set(expandedYears);
    if (newExpandedYears.has(year)) {
      newExpandedYears.delete(year);
    } else {
      newExpandedYears.add(year);
    }
    setExpandedYears(newExpandedYears);
  };
  
  // Reusable component to display member cards
  const MemberCardGrid = ({ members, emptyMessage, isCompact = false }) => {
    if (members.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-color)' }}>
          {emptyMessage}
        </div>
      );
    }
    
    const gridColumns = isCompact ? 'repeat(auto-fill, minmax(200px, 1fr))' : 'repeat(auto-fill, minmax(300px, 1fr))';
    const cardHeight = isCompact ? '180px' : '250px';
    const cardPadding = isCompact ? '1rem' : '1.5rem';
    const titleSize = isCompact ? '1.1rem' : '1.3rem';
    const positionSize = isCompact ? '0.9rem' : '1rem';
    const bioSize = isCompact ? '0.8rem' : '0.9rem';
    
    return (
      <div className="office-bearers-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: gridColumns, 
        gap: isCompact ? '1.5rem' : '2rem',
        marginTop: '2rem'
      }}>
        {members.map(bearer => (
          <div className="office-bearer-card" key={bearer.id} style={{ 
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: 'var(--box-shadow)'
          }}>
            <div className="bearer-image" style={{ 
              height: cardHeight,
              backgroundImage: `url(${getResourceUrl(bearer.image_url || bearer.image)})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#f8f8f8'
            }}></div>
            <div className="bearer-content" style={{ padding: cardPadding }}>
              <h3 style={{ fontSize: titleSize, marginBottom: '0.3rem', color: 'var(--primary-color)' }}>
                {bearer.name}
              </h3>
              <p style={{ 
                fontSize: positionSize, 
                fontWeight: '600', 
                color: 'var(--secondary-color)',
                marginBottom: isCompact ? '0.5rem' : '1rem'
              }}>
                {bearer.position}
              </p>
              {!isCompact && (
                <p style={{ fontSize: bioSize, lineHeight: '1.6', marginBottom: '1rem' }}>
                  {bearer.bio || 'No biography available.'}
                </p>
              )}
              {bearer.linkedin_url && (
                <div style={{ marginTop: isCompact ? '0.5rem' : '1rem' }}>
                  <a 
                    href={bearer.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#0077b5',
                      textDecoration: 'none',
                      fontSize: isCompact ? '0.8rem' : '0.9rem',
                      fontWeight: '500',
                      padding: isCompact ? '0.3rem 0.8rem' : '0.5rem 1rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '20px',
                      border: '1px solid #e9ecef',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#0077b5';
                      e.target.style.color = 'white';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#0077b5';
                    }}
                  >
                    <FontAwesomeIcon icon={faLinkedin} style={{ marginRight: '0.5rem' }} />
                    {isCompact ? 'LinkedIn' : 'Connect on LinkedIn'}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Component to display year-wise past office bearers
  const YearWiseOfficeBearers = () => {
    if (pastOfficeBearersByYear.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-color)' }}>
          No past committee members found.
        </div>
      );
    }

    return (
      <div className="year-wise-bearers">
        {pastOfficeBearersByYear.map((yearData) => (
          <div key={yearData.year} className="year-section" style={{ marginBottom: '3rem' }}>
            <div 
              onClick={() => toggleYearExpansion(yearData.year)}
              style={{
                cursor: 'pointer',
                padding: '1rem',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                borderRadius: '8px',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'var(--box-shadow)'
              }}
            >
              <h3 style={{ fontSize: '1.5rem', margin: 0 }}>
                Committee Members
                {(() => {
                  // Try to get term dates from office bearers first, then advisors
                  const memberWithDates = yearData.office_bearers.find(member => member.term_start && member.term_end) || 
                                         yearData.advisors.find(member => member.term_start && member.term_end);
                  
                  if (memberWithDates) {
                    return (
                      <span style={{ fontSize: '1.1rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                        ({new Date(memberWithDates.term_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} to {new Date(memberWithDates.term_end).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                      </span>
                    );
                  }
                  return null;
                })()}
              </h3>
              <span style={{ fontSize: '1.2rem' }}>
                {expandedYears.has(yearData.year) ? '−' : '+'}
              </span>
            </div>
            
            {expandedYears.has(yearData.year) && (
              <div className="year-content">
                {/* Office Bearers */}
                {yearData.office_bearers.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h4 style={{ 
                      fontSize: '1.2rem', 
                      marginBottom: '1rem',
                      color: 'var(--primary-color)',
                      borderBottom: '2px solid var(--primary-color)',
                      paddingBottom: '0.5rem'
                    }}>
                      Committee Members
                    </h4>
                    <MemberCardGrid 
                      members={yearData.office_bearers}
                      emptyMessage="No committee members found for this year."
                      isCompact={true}
                    />
                  </div>
                )}
                
                {/* Advisors */}
                {yearData.advisors.length > 0 && (
                  <div>
                    <h4 style={{ 
                      fontSize: '1.2rem', 
                      marginBottom: '1rem',
                      color: 'var(--secondary-color)',
                      borderBottom: '2px solid var(--secondary-color)',
                      paddingBottom: '0.5rem'
                    }}>
                      Advisors
                    </h4>
                    <MemberCardGrid 
                      members={yearData.advisors}
                      emptyMessage="No advisors found for this year."
                      isCompact={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="page-container">
      <Header />
      
      <div className="page-header">
        <h1>OFFICE BEARERS</h1>
        <p>Meet the dedicated team leading Bangalore Valuers' Association</p>
      </div>
      
      <section className="content-section">
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              onClick={() => handleViewModeChange('current')}
              style={{
                padding: '0.8rem 1.5rem',
                backgroundColor: viewMode === 'current' ? 'var(--primary-color)' : '#f0f0f0',
                color: viewMode === 'current' ? 'white' : 'var(--text-color)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: viewMode === 'current' ? 'bold' : 'normal',
                marginRight: '1rem'
              }}
            >
              Current Committee
            </button>
            <button
              onClick={() => handleViewModeChange('past')}
              style={{
                padding: '0.8rem 1.5rem',
                backgroundColor: viewMode === 'past' ? 'var(--primary-color)' : '#f0f0f0',
                color: viewMode === 'past' ? 'white' : 'var(--text-color)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: viewMode === 'past' ? 'bold' : 'normal'
              }}
            >
              Past Committee Members
            </button>
          </div>
          
          <p style={{ marginTop: '1rem' }}>
            Viewing <strong>{viewMode === 'current' ? 'current office bearers' : 'past committee members'}</strong>
          </p>
        </div>
        
        <h2>{viewMode === 'current' ? 'OUR LEADERSHIP TEAM' : 'PAST COMMITTEE MEMBERS'}</h2>
        {viewMode === 'current' && currentTermLabel && (
          <p style={{ fontWeight: '600', color: 'var(--secondary-color)', marginBottom: '0.5rem' }}>
            Term: {currentTermLabel}
          </p>
        )}
        <p>
          {viewMode === 'current' 
            ? 'The Bangalore Valuers Association is led by a team of experienced and dedicated valuation professionals who are committed to advancing the profession and supporting our members. Our office bearers bring diverse expertise and perspectives to the association, ensuring that we address the varied needs of our members and the profession as a whole.'
            : 'We honor and acknowledge the dedicated service of our past committee members who have contributed significantly to the growth and development of the Bangalore Valuers Association. Their leadership and vision have helped shape the association into what it is today.'
          }
        </p>
        
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading {viewMode === 'current' ? 'office bearers' : 'past committee members'}...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--error-color)' }}>
            {error}
          </div>
        ) : (
          <>
            {viewMode === 'current' ? (
              <>
                <MemberCardGrid 
                  members={officeBearers}
                  emptyMessage="No office bearers found."
                />
                
                {/* Advisors Section */}
                {advisors.length > 0 && (
                  <div style={{ marginTop: '4rem' }}>
                    <h2>OUR ADVISORY BOARD</h2>
                    <p>
                      Our association benefits from the wisdom and guidance of experienced professionals who serve as advisors. Their expertise helps shape our strategic direction and initiatives.
                    </p>
                    
                    <MemberCardGrid 
                      members={advisors}
                      emptyMessage="No advisors found."
                    />
                  </div>
                )}
              </>
            ) : (
              <YearWiseOfficeBearers />
            )}
          </>
        )}
        
        <div style={{ marginTop: '3rem' }}>
          <h2>OUR GOVERNANCE</h2>
          <p>
            The Bangalore Valuers' Association is governed by a constitution that outlines 
            our objectives, membership criteria, and governance structure. Our office 
            bearers are elected by the members for a term of one year, ensuring democratic 
            representation and fresh perspectives in the leadership.
          </p>
          <p>
            The Executive Committee, comprising the office bearers, meets regularly to 
            discuss and decide on matters related to the association's activities, 
            programs, and initiatives. The committee is responsible for implementing 
            the association's strategic plan and ensuring that we fulfill our mission 
            of supporting valuation professionals.
          </p>
        </div>
        
        <div style={{ 
          backgroundColor: 'var(--dark-blue-bg)', 
          color: 'white', 
          padding: '2rem', 
          borderRadius: '8px', 
          marginTop: '2rem',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>
            Interested in Joining the Leadership Team?
          </h3>
          <p style={{ marginBottom: '1.5rem', maxWidth: '800px', margin: '0 auto 1.5rem' }}>
            We are always looking for dedicated professionals to contribute to the association's growth and development. If you are interested in volunteering for specific initiatives, please get in touch with us.
          </p>
          <Link to="/contact" className="btn btn-light" style={{textDecoration: 'none'}}>CONTACT US</Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default OfficeBearersPage;